import { Autocomplete } from "@mui/material";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/BootstrapTextField";
import SubmitButton from "components/Buttons/SubmitButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import { City, Country, State } from "country-state-city";
import React, { useCallback, useEffect, useState } from "react";
import { autocompleteDropdownStyles } from "Constants/Style";
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageContainer from "components/PageContainer/PageContainer";

const commonLabelStyle = { fontSize: "0.8rem", marginBottom: 4 };

const customerTypes = [{ label: "B2B" }, { label: "B2C" }];

const CustomerForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    customer_name: "",
    company_name: "",
    customer_type: "",
    email: "",
    contact_number: "",
    aadhar_no: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    gstin: "",

    contact_person_name: "",
    contact_person_mobile_no: "",

    customer_name_for_transport: "",
    transporter_name: "",
    transporter_contact_person_name: "",
    transporter_contact_number: "",
    transporter_address: "",
    transporter_city: "",
    transporter_state: "",
    transporter_country: "",
    transporter_pincode: "",
  });

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isLoadingStates, setIsLoadingStates] = useState(false);

  // Transporter address and loading states
  const [transporter_countries, setTransporterCountries] = useState<any[]>([]);
  const [transporter_states, setTransporterStates] = useState<any[]>([]);
  const [transporter_cities, setTransporterCities] = useState<any[]>([]);
  const [isLoadingCitiesTransporter, setTransporterIsLoadingCities] =
    useState(false);
  const [isLoadingStatesTransporter, setTransporterIsLoadingStates] =
    useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState<any>(null);

  // Validation rules
  const validate = () => {
    const errors: Record<string, string> = {};
    const {
      customer_name,
      customer_type,
      email,
      contact_number,
      aadhar_no,
      address,
      city,
      state,
      country,
      pincode,
      contact_person_name,
      contact_person_mobile_no,
      gstin,
    } = formData;

    if (!customer_name.trim())
      errors.customer_name = "Customer name is required";
    if (!customer_type.trim())
      errors.customer_type = "Customer type is required";
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email))
      errors.email = "Valid email is required";
    if (!/^\d{10}$/.test(contact_number))
      errors.contact_number = "Valid 10-digit contact number required";

    // Aadhar required
    if (!aadhar_no || !/^\d{12}$/.test(aadhar_no))
      errors.aadhar_no = "Aadhar must be 12 digits";

    if (!address.trim()) errors.address = "Address is required";

    // city, state, country required
    if (!city || !city.trim()) errors.city = "City is required";
    if (!state || !state.trim()) errors.state = "State is required";
    if (!country || !country.trim()) errors.country = "Country is required";

    if (!/^\d{6}$/.test(pincode)) errors.pincode = "Pincode must be 6 digits";

    if (!contact_person_name.trim())
      errors.contact_person_name = "Contact person name is required";
    if (!/^\d{10}$/.test(contact_person_mobile_no))
      errors.contact_person_mobile_no = "Valid 10-digit number required";

    // GSTIN required
    const gstinValue = gstin.toUpperCase();
    const gstinRegex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gstinValue) {
      errors.gstin = "GSTIN is required";
    } else if (!gstinRegex.test(gstinValue)) {
      errors.gstin = "GSTIN must follow the correct format";
    }

    return errors;
  };

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));

      // Remove the error for this field as user types
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    },

    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please enter all required fields correctly.");
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      let response;
      if (id) {
        response = await Axios.put(`/master/customer/${id}/update/`, formData);
      } else {
        response = await Axios.post("/master/customer/create/", formData);
      }

      if (response.status === 200 || response.status === 201) {
        toast.success(`Customer ${id ? "updated" : "created"} successfully`);
        // setTimeout(() => navigate("/customer"), 1000);
        navigate("/customer", { state: { clearState: !id } });
      } else {
        toast.error(`Failed to ${id ? "update" : "create"} Customer`);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.error ||
        error?.message ||
        "Failed to save Customer";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Populate country dropdown
  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);
    setTransporterCountries(countryList); // For Transporter

    // Auto-select India when creating a new customer (not in edit mode)
    if (!id && countryList.length > 0) {
      const india = countryList.find(
        (c: any) => c.name === "India" || c.isoCode === "IN"
      );
      if (india) {
        setFormData((prev) => ({
          ...prev,
          country: india.name,
        }));
        // Load states for India
        const indiaStates = State.getStatesOfCountry(india.isoCode);
        setStates(indiaStates);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Edit mode → fetch existing record
  useEffect(() => {
    if (id) {
      setLoading(true);
      Axios.get(`/master/customer/${id}/get/`)
        .then((res) => {
          const response = res?.data?.data || {};

          if (Object.keys(response).length > 0) {
            // Country & State pre-population
            const selectedCountry = countries.find(
              (c) => c.name === response.country
            );
            const countryIsoCode = selectedCountry?.isoCode || "";
            const statesOfCountry = State.getStatesOfCountry(countryIsoCode);

            const selectedState = statesOfCountry.find(
              (s) => s.name === response.state
            );
            const stateIsoCode = selectedState?.isoCode || "";
            const citiesOfState = City.getCitiesOfState(
              countryIsoCode,
              stateIsoCode
            );

            setStates(statesOfCountry);
            setCities(citiesOfState);

            // --- Transporter ---
            // Use transporter_country from response (check both possible field names)
            const transporterCountryName =
              response.transporter_country || response.transport_country || "";
            const transporterCountry = transporter_countries.find(
              (c) => c.name === transporterCountryName
            );
            const transporterCountryIso = transporterCountry?.isoCode || "";

            if (transporterCountryIso) {
              const transporterStatesOfCountry = State.getStatesOfCountry(
                transporterCountryIso
              );

              // Use transporter_state from response (check both possible field names)
              const transporterStateName =
                response.transporter_state || response.transport_state || "";
              const transporterState = transporterStatesOfCountry.find(
                (s) => s.name === transporterStateName
              );
              const transporterStateIso = transporterState?.isoCode || "";

              // Only get cities if we have a valid state ISO code
              if (transporterStateIso) {
                const transporterCitiesOfState = City.getCitiesOfState(
                  transporterCountryIso,
                  transporterStateIso
                );
                setTransporterCities(transporterCitiesOfState);
              } else {
                // If state not found, set empty array
                setTransporterCities([]);
              }

              setTransporterStates(transporterStatesOfCountry);
            } else {
              // If country not found, set empty arrays
              setTransporterStates([]);
              setTransporterCities([]);
            }

            setFormData({
              // --- Customer fields ---
              customer_name: response.customer_name || "",
              company_name: response.company_name || "",
              customer_type: response.customer_type || "",
              email: response.email || "",
              contact_number: response.contact_number || "",
              aadhar_no: response.aadhar_no || "",
              address: response.address || "",
              city: response.city || "",
              state: response.state || "",
              country: response.country || "",
              pincode: response.pincode || "",
              contact_person_name: response.contact_person_name || "",
              contact_person_mobile_no: response.contact_person_mobile_no || "",
              gstin: response.gstin || "",

              // --- Transporter fields (optional) ---
              customer_name_for_transport:
                response.customer_name_for_transport || "",
              transporter_name: response.transporter_name || "",
              transporter_contact_person_name:
                response.transporter_contact_person_name || "",
              transporter_contact_number:
                response.transporter_contact_number || "",
              transporter_address: response.transporter_address || "",
              transporter_city: response.transporter_city || "",
              transporter_state: response.transporter_state || "",
              transporter_country: response.transporter_country || "",
              transporter_pincode: response.transporter_pincode || "",
            });
          }
        })
        .catch((err) => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [id, countries, transporter_countries]);

  const LoadingCard = () => (
    <div className="text-center py-5 bg-white rounded-3 shadow-sm">
      <Spinner animation="border" variant="primary" />
    </div>
  );

  return (
    <PageContainer>
      <FormHeader
        title={`${id ? "Edit" : "Add"} Customer`}
        firstLabel="Customer"
        secondLabel={id ? "Edit Customer" : "Add Customer"}
        handleArrowClick={() => navigate("/customer")}
      />

      {loading ? (
        <LoadingCard />
      ) : (
        <Form noValidate onSubmit={handleSubmit}>
          <p>
            <h6 className="card-title ">Customer Details</h6>
          </p>
          <Row className="mb-4 gy-3">
            {/* Customer Name */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">
                Customer Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                isInvalid={!!errors.customer_name}
                placeholder="Enter Name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.customer_name}
              </Form.Control.Feedback>
            </Col>

            {/* Company Name */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">Company Name</Form.Label>
              <Form.Control
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                isInvalid={!!errors.company_name}
                placeholder="Enter Name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.company_name}
              </Form.Control.Feedback>
            </Col>

            {/* Customer Type */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">
                Customer Type <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={customerTypes}
                getOptionLabel={(option) => option.label}
                value={
                  customerTypes.find(
                    (c) => c.label === formData.customer_type
                  ) || null
                }
                onChange={(_, selected) => {
                  setFormData((prev) => ({
                    ...prev,
                    customer_type: selected?.label || "",
                  }));

                  setErrors((prev) => ({
                    ...prev,
                    customer_type: undefined, // clear the error
                  }));
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.label
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Customer Type"
                    size="small"
                    error={!!errors.customer_type}
                    helperText={errors.customer_type}
                    fullWidth
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
              />
            </Col>

            {/* Email */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">
                Email <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                isInvalid={!!errors.email}
                placeholder="Enter Email"
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </Col>

            {/* Contact No. */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">
                Contact <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="contact_number"
                value={formData.contact_number}
                onChange={handleChange}
                isInvalid={!!errors.contact_number}
                placeholder="10-digit number"
              />
              <Form.Control.Feedback type="invalid">
                {errors.contact_number}
              </Form.Control.Feedback>
            </Col>

            {/* Aadhaar No. */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">
                Aadhaar No. <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="aadhar_no"
                value={formData.aadhar_no}
                onChange={handleChange}
                isInvalid={!!errors.aadhar_no}
                placeholder="12-digit number"
              />
              <Form.Control.Feedback type="invalid">
                {errors.aadhar_no}
              </Form.Control.Feedback>
            </Col>
          </Row>

          <hr />

          <Row className="mb-4">
            <p>
              <h6 className="card-title mt-3">Contact Person Details</h6>
            </p>
            {/* Contact Person Name */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">
                Contact Person Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="contact_person_name"
                value={formData.contact_person_name}
                onChange={handleChange}
                isInvalid={!!errors.contact_person_name}
                placeholder="Enter Name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.contact_person_name}
              </Form.Control.Feedback>
            </Col>

            {/* Contact Person Number */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">
                Contact Person Number <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="contact_person_mobile_no"
                value={formData.contact_person_mobile_no}
                onChange={handleChange}
                isInvalid={!!errors.contact_person_mobile_no}
                placeholder="10-digit number"
              />
              <Form.Control.Feedback type="invalid">
                {errors.contact_person_mobile_no}
              </Form.Control.Feedback>
            </Col>
          </Row>

          <hr />

          <Row className="mb-4 gy-3">
            <p>
              <h6 className="card-title mt-3">Customer Address Details</h6>
            </p>
            {/* Address */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold mb-1">
                Address <span className="text-danger">*</span>
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  isInvalid={!!errors.address}
                  placeholder="Enter Address"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>

            {/* Country */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Country <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={countries}
                getOptionLabel={(o) => o.name}
                value={
                  countries.find((c) => c.name === formData.country) || null
                }
                onChange={(_, selected) => {
                  setFormData((prev) => ({
                    ...prev,
                    country: selected?.name || "",
                    state: "",
                    city: "",
                  }));

                  setErrors((prev) => ({ ...prev, country: undefined }));

                  if (selected) {
                    setIsLoadingStates(true);
                    setStates(State.getStatesOfCountry(selected.isoCode));
                    setCities([]);
                    setIsLoadingStates(false);
                  }
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Country"
                    size="small"
                    error={!!errors.country}
                    helperText={errors.country}
                    fullWidth
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
              />
            </Col>

            {/* State */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                State <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={states}
                getOptionLabel={(o) => o.name}
                value={states.find((s) => s.name === formData.state) || null}
                onChange={(_, selected) => {
                  const country = countries.find(
                    (c) => c.name === formData.country
                  );
                  setFormData((prev) => ({
                    ...prev,
                    state: selected?.name || "",
                    city: "",
                  }));

                  setErrors((prev) => ({ ...prev, state: undefined }));

                  if (country && selected) {
                    setIsLoadingCities(true);
                    setCities(
                      City.getCitiesOfState(country.isoCode, selected.isoCode)
                    );
                    setIsLoadingCities(false);
                  }
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    // placeholder="Select State"
                    placeholder={
                      states.length > 0
                        ? "Select State"
                        : "Select Country First"
                    }
                    size="small"
                    error={!!errors.state}
                    helperText={errors.state}
                    fullWidth
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
                loading={isLoadingStates}
                disabled={!formData.country}
              />
            </Col>

            {/* City */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                City <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={cities}
                getOptionLabel={(o) => o.name}
                value={cities.find((c) => c.name === formData.city) || null}
                onChange={(_, selected) => {
                  setFormData((prev) => ({
                    ...prev,
                    city: selected?.name || "",
                  }));

                  // Clear error when a valid city is selected
                  setErrors((prev) => ({
                    ...prev,
                    city: undefined,
                  }));
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder={
                      states.length > 0 ? "Select City" : "Select State First"
                    }
                    size="small"
                    error={!!errors.city}
                    helperText={errors.city}
                    fullWidth
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
                loading={isLoadingCities}
                disabled={!formData.state}
              />
            </Col>

            {/* Pincode */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">
                Pincode <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={(e) => {
                  const numeric = e.target.value.replace(/\D/g, "");

                  setFormData((prev) => ({ ...prev, pincode: numeric }));

                  // Clear error if the new value is valid
                  if (/^\d{6}$/.test(numeric)) {
                    setErrors((prev) => ({ ...prev, pincode: undefined }));
                  }
                }}
                isInvalid={!!errors.pincode}
                placeholder="6-digit Pincode"
              />
              <Form.Control.Feedback type="invalid">
                {errors.pincode}
              </Form.Control.Feedback>
            </Col>

            {/* GSTIN */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">
                GSTIN <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="gstin"
                value={formData.gstin}
                onChange={handleChange}
                isInvalid={!!errors.gstin}
                placeholder="15-char GSTIN"
              />
              <Form.Control.Feedback type="invalid">
                {errors.gstin}
              </Form.Control.Feedback>
            </Col>
          </Row>

          <hr />

          {/* Transporter Details (Optional) */}
          <Row className="mb-4 gy-3">
            <p>
              <h6 className="card-title mt-3">
                Transporter Details (Optional)
              </h6>
            </p>

            {/* Customer Name For Transport */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">
                Customer Name For Transport
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  name="customer_name_for_transport"
                  value={formData.customer_name_for_transport}
                  onChange={handleChange}
                  isInvalid={!!errors.customer_name_for_transport}
                  placeholder="Enter Customer Name For Transport"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.customer_name_for_transport}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>

            {/* Transporter Name */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">Transporter Name</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  name="transporter_name"
                  value={formData.transporter_name}
                  onChange={handleChange}
                  isInvalid={!!errors.transporter_name}
                  placeholder="Enter Transporter Name"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.transporter_name}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>

            {/* Contact Person Name */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">
                Contact Person Name
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  name="transporter_contact_person_name"
                  value={formData.transporter_contact_person_name}
                  onChange={handleChange}
                  isInvalid={!!errors.transporter_contact_person_name}
                  placeholder="Enter Contact Person Name"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.transporter_contact_person_name}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>

            {/* Contact Person Number */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">
                Contact Person Number
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  name="transporter_contact_number"
                  value={formData.transporter_contact_number}
                  onChange={handleChange}
                  isInvalid={!!errors.transporter_contact_number}
                  placeholder="Enter Contact Person Number"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.transporter_contact_number}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>

            {/* Transporter Address */}
            <Col xs={12} sm={12} lg={6}>
              <Form.Label className="fw-semibold">Address</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  name="transporter_address"
                  value={formData.transporter_address}
                  onChange={handleChange}
                  isInvalid={!!errors.transporter_address}
                  placeholder="Enter Address"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.transporter_address}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>

            {/* Transporter Country */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Country
              </Form.Label>
              <Autocomplete
                options={transporter_countries}
                getOptionLabel={(o) => o.name}
                value={
                  transporter_countries.find(
                    (c) => c.name === formData.transporter_country
                  ) || null
                }
                onChange={(_, selected) => {
                  setFormData((prev) => ({
                    ...prev,
                    transporter_country: selected?.name || "",
                    transporter_state: "",
                    transporter_city: "",
                  }));
                  if (selected) {
                    setTransporterIsLoadingStates(true);
                    setTransporterStates(
                      State.getStatesOfCountry(selected.isoCode)
                    );
                    setTransporterCities([]);
                    setTransporterIsLoadingStates(false);
                  }
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Country"
                    size="small"
                    error={!!errors.transporter_country}
                    helperText={errors.transporter_country}
                    fullWidth
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
              />
            </Col>

            {/* Transporter State */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                State
              </Form.Label>
              <Autocomplete
                options={transporter_states}
                getOptionLabel={(o) => o.name}
                value={
                  transporter_states.find(
                    (s) => s.name === formData.transporter_state
                  ) || null
                }
                onChange={(_, selected) => {
                  const country = transporter_countries.find(
                    (c) => c.name === formData.transporter_country
                  );
                  setFormData((prev) => ({
                    ...prev,
                    transporter_state: selected?.name || "",
                    transporter_city: "",
                  }));
                  if (country && selected) {
                    setTransporterIsLoadingCities(true);
                    setTransporterCities(
                      City.getCitiesOfState(country.isoCode, selected.isoCode)
                    );
                    setTransporterIsLoadingCities(false);
                  }
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder={
                      transporter_states.length > 0
                        ? "Select State"
                        : "Select Country First"
                    }
                    size="small"
                    error={!!errors.transporter_state}
                    helperText={errors.transporter_state}
                    fullWidth
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
                loading={isLoadingStatesTransporter}
                disabled={!formData.transporter_country}
              />
            </Col>

            {/* Transporter City */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                City
              </Form.Label>
              <Autocomplete
                options={transporter_cities}
                getOptionLabel={(o) => o.name}
                value={
                  transporter_cities.find(
                    (c) => c.name === formData.transporter_city
                  ) || null
                }
                onChange={(_, selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    transporter_city: selected?.name || "",
                  }))
                }
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder={
                      transporter_states.length > 0
                        ? "Select City"
                        : "Select State First"
                    }
                    size="small"
                    // error={!!errors.transport_city}
                    helperText={errors.transporter_city}
                    fullWidth
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
                loading={isLoadingCitiesTransporter}
                disabled={!formData.transporter_state}
              />
            </Col>

            {/* Transporter Pincode */}
            <Col xs={12} sm={6} lg={3}>
              <Form.Label className="fw-semibold">Pincode</Form.Label>
              <Form.Control
                type="text"
                name="transporter_pincode"
                value={formData.transporter_pincode}
                onChange={(e) => {
                  const numeric = e.target.value.replace(/\D/g, "");
                  setFormData((prev) => ({
                    ...prev,
                    transporter_pincode: numeric,
                  }));
                }}
                isInvalid={!!errors.transport_pincode}
                placeholder="6-digit Pincode"
              />
              <Form.Control.Feedback type="invalid">
                {errors.transport_pincode}
              </Form.Control.Feedback>
            </Col>
          </Row>

          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelBackButton to={-1} />

            {/* Reusable submit button with spinner */}
            <SubmitButton isSubmitting={isSubmitting} isEdit={!!id} />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default CustomerForm;
